import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks';
import { storage } from '@/utils';
import Button from '@/components/common/Button/Button';
import styles from './PaymentBanner.module.css';

/**
 * PaymentBanner Component
 * Displays payment methods, cryptocurrencies, and deposit button
 * Responsive design for mobile and web
 */

const cryptocurrencies = [
  { name: 'BTC', icon: '/assets/images/coins/BTC.webp' },
  { name: 'ETH', icon: '/assets/images/coins/ETH.webp' },
  { name: 'USDT', icon: '/assets/images/coins/USDT.webp' },
  { name: 'LTC', icon: '/assets/images/coins/LTC.webp' },
  { name: 'BCH', icon: '/assets/images/coins/BCH.webp' },
  { name: 'DOGE', icon: '/assets/images/coins/DOGE.webp' },
  { name: 'BNB', icon: '/assets/images/coins/BNB.webp' },
  { name: 'XRP', icon: '/assets/images/coins/XRP.webp' },
  { name: 'USDC', icon: '/assets/images/coins/USDC.webp' },
  { name: 'ADA', icon: '/assets/images/coins/ADA.webp' },
];

const paymentMethods = [
  { name: 'Apple Pay', icon: '/assets/images/payment-method/apple-pay.svg' },
  { name: 'Google Pay', icon: '/assets/images/payment-method/google-pay.svg' },
  { name: 'Visa', icon: '/assets/images/payment-method/visa.svg' },
  { name: 'Mastercard', icon: '/assets/images/payment-method/master.svg' },
];

const PaymentBanner = () => {
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Check login status
    const logged = storage.getKey('logged') === true || localStorage.getItem('logged') === 'true';
    setIsLoggedIn(logged);
  }, []);

  const handleDeposit = () => {
    if (!isLoggedIn) {
      // Open signup modal for non-logged-in users
      navigate('/login?modalType=auth-signup');
    } else {
      // Navigate to wallet modal with deposit tab for logged-in users
      navigate('/wallet?mode=deposit');
    }
  };

  if (isMobile) {
    // Mobile View - Vertical Layout
    return (
      <div className={styles.container}>
        <div className={styles.mobileWrapper}>
          {/* Cryptocurrency Logos */}
          <div className={styles.cryptoSection}>
            {cryptocurrencies.slice(0, 7).map((crypto) => (
              <img
                key={crypto.name}
                src={crypto.icon}
                alt={crypto.name}
                className={styles.cryptoIcon}
                loading="lazy"
              />
            ))}
          </div>

          {/* Payment Method Logos */}
          <div className={styles.paymentSection}>
            <ul className={styles.paymentList}>
              {paymentMethods.map((method) => (
                <li key={method.name}>
                  <img
                    src={method.icon}
                    alt={method.name}
                    className={styles.paymentIcon}
                    loading="lazy"
                  />
                </li>
              ))}
            </ul>
          </div>

          {/* Deposit Button */}
          <Button
            variant="primary"
            size="md"
            onClick={handleDeposit}
            className={styles.depositButton}
          >
            Deposit Now
          </Button>
        </div>
      </div>
    );
  }

  // Web View - Horizontal Layout
  return (
    <div className={styles.container}>
      <div className={styles.webWrapper}>
        {/* Left Section: Bonus + Payment Methods + Cryptocurrencies */}
        <div className={styles.leftSection}>
          {/* Bonus Text */}
          <div className={styles.bonusSection}>
            <img
              src="/assets/images/logo/mobile-logo.webp"
              alt="Bonus Icon"
              className={styles.bonusIcon}
            />
            <span className={styles.bonusText}>
              <span className={styles.bonusAmount}>200%</span> Welcome Bonus
            </span>
          </div>

          {/* Payment Methods */}
          <div className={styles.paymentMethodsWeb}>
            {paymentMethods.map((method) => (
              <img
                key={method.name}
                src={method.icon}
                alt={method.name}
                className={styles.paymentIconWeb}
                loading="lazy"
              />
            ))}
          </div>

          {/* Cryptocurrencies */}
          <div className={styles.cryptoSectionWeb}>
            {cryptocurrencies.map((crypto) => (
              <img
                key={crypto.name}
                src={crypto.icon}
                alt={crypto.name}
                className={styles.cryptoIconWeb}
                loading="lazy"
              />
            ))}
          </div>
        </div>

        {/* Right Section: Deposit Button */}
        <div className={styles.rightSection}>
          <Button
            variant="primary"
            size="md"
            onClick={handleDeposit}
            className={styles.depositButtonWeb}
          >
            Deposit Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentBanner;
